import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { api, type OverviewResponse } from "./api";

interface SystemState {
  overview: OverviewResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const Ctx = createContext<SystemState>({
  overview: null,
  loading: true,
  error: null,
  refresh: async () => {},
});

/**
 * Shared 30s-polled system snapshot. The sidebar status footer and the
 * Overview page consume the same data — one poller, no duplicate traffic.
 */
export function SystemProvider({ children }: { children: ReactNode }) {
  const [overview, setOverview] = useState<OverviewResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const o = await api<OverviewResponse>("/api/overview");
      setOverview(o);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load overview");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const t = setInterval(() => void refresh(), 30_000);
    return () => clearInterval(t);
  }, [refresh]);

  return (
    <Ctx.Provider value={{ overview, loading, error, refresh }}>
      {children}
    </Ctx.Provider>
  );
}

export function useSystem(): SystemState {
  return useContext(Ctx);
}
